import { Chess } from "chess.js";

export interface LegalMove {
  uci: string;
  san: string;
  from: string;
  to: string;
  piece: string;
  captured?: string;
}

export interface PositionView {
  fen: string;
  sideToMove: "white" | "black";
  inCheck: boolean;
  legalMoves: LegalMove[];
}

export function readPosition(fen: string): PositionView | null {
  try {
    const chess = new Chess(fen);
    const legalMoves = chess.moves({ verbose: true }).map((move) => ({
      uci: `${move.from}${move.to}${move.promotion ?? ""}`,
      san: move.san,
      from: move.from,
      to: move.to,
      piece: move.piece,
      captured: move.captured,
    }));

    return {
      fen: chess.fen(),
      sideToMove: chess.turn() === "w" ? "white" : "black",
      inCheck: chess.inCheck(),
      legalMoves,
    };
  } catch {
    return null;
  }
}

export function legalMovesOf(fen: string): LegalMove[] {
  return readPosition(fen)?.legalMoves ?? [];
}

export interface AppliedMove {
  fen: string;
  san: string;
  uci: string;
  legal: boolean;
}

type PromotionPiece = "q" | "r" | "b" | "n" | "p";

function promotionOf(uci: string): PromotionPiece | undefined {
  const piece = uci.slice(4, 5);
  return piece === "q" || piece === "r" || piece === "b" || piece === "n" || piece === "p"
    ? piece
    : undefined;
}

export function applyUciMove(fen: string, uci: string): AppliedMove {
  const chess = new Chess(fen);
  try {
    const move = chess.move({
      from: uci.slice(0, 2),
      to: uci.slice(2, 4),
      promotion: promotionOf(uci),
    });
    if (!move) return { fen, san: "", uci, legal: false };
    return {
      fen: chess.fen(),
      san: move.san,
      uci: `${move.from}${move.to}${move.promotion ?? ""}`,
      legal: true,
    };
  } catch {
    return { fen, san: "", uci, legal: false };
  }
}

export function fenSideToMove(fen: string): "white" | "black" {
  return fen.split(" ")[1] === "b" ? "black" : "white";
}

export function uciLineToSan(fen: string, uciMoves: string[]): string[] {
  const sans: string[] = [];
  let current = fen;
  for (const uci of uciMoves) {
    const applied = applyUciMove(current, uci);
    if (!applied.legal) break;
    sans.push(applied.san);
    current = applied.fen;
  }
  return sans;
}

export function applySanMove(fen: string, san: string): AppliedMove {
  const chess = new Chess(fen);
  try {
    const move = chess.move(san);
    if (!move) return { fen, san: "", uci: "", legal: false };
    return {
      fen: chess.fen(),
      san: move.san,
      uci: `${move.from}${move.to}${move.promotion ?? ""}`,
      legal: true,
    };
  } catch {
    return { fen, san: "", uci: "", legal: false };
  }
}
